import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SEOHead } from '../../../components/seo/SEOHead';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select, Textarea } from '../../../components/ui/Input';
import { api } from '../../../lib/api';
import { ORDER_STATUS_LABELS, type Order, type OrderStatus } from '@bharat-consultancy/shared';

interface AdminNote {
  id: string;
  note: string;
  admin_id: string;
  created_at: string;
}

export default function AdminOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [notes, setNotes] = useState<AdminNote[]>([]);
  const [allowedTransitions, setAllowedTransitions] = useState<OrderStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');

  function loadOrder() {
    if (!id) return Promise.resolve();
    return api.admin.orders.get(id).then((res) => {
      setOrder(res.order);
      setNotes(res.notes as AdminNote[]);
    });
  }

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setLoadError(null);
    Promise.all([
      loadOrder(),
      api.admin.orders.allowedTransitions(id).then((res) => setAllowedTransitions(res.allowedTransitions as OrderStatus[])),
    ])
      .catch(() => setLoadError('Could not load this order. Please refresh and try again.'))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleStatusChange(newStatus: OrderStatus) {
    if (!id) return;
    setStatusUpdating(true);
    setActionError(null);
    try {
      await api.admin.orders.updateStatus(id, newStatus);
      await loadOrder();
      const transitions = await api.admin.orders.allowedTransitions(id);
      setAllowedTransitions(transitions.allowedTransitions as OrderStatus[]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not update order status.');
    } finally {
      setStatusUpdating(false);
    }
  }

  async function handleAddNote(e: FormEvent) {
    e.preventDefault();
    if (!id || !newNote.trim()) return;
    setActionError(null);
    try {
      await api.admin.orders.addNote(id, newNote.trim());
      setNewNote('');
      await loadOrder();
    } catch {
      setActionError('Could not add note. Please try again.');
    }
  }

  async function handleResendInvoice() {
    if (!id) return;
    setActionError(null);
    try {
      await api.admin.orders.resendInvoice(id);
      alert('Invoice resent.');
    } catch {
      setActionError('Could not resend invoice. Please try again.');
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-seal-blue" />
      </div>
    );
  }

  if (loadError) return <p className="text-red-600">{loadError}</p>;
  if (!order) return <p className="text-ink/60">Order not found.</p>;

  return (
    <>
      <SEOHead title={`Order ${order.orderNumber} — Admin`} description="" canonicalPath={`/admin/orders/${order.id}`} />

      {actionError && <p className="mb-4 text-sm text-red-600">{actionError}</p>}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">{order.orderNumber}</h1>
          <p className="mt-1 text-ink/60">{order.service?.title ?? 'Service'}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Customer Details</h2>
            <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-ink/50">Name</dt>
                <dd className="text-ink">{order.contactName}</dd>
              </div>
              <div>
                <dt className="text-ink/50">Email</dt>
                <dd className="text-ink">{order.contactEmail}</dd>
              </div>
              <div>
                <dt className="text-ink/50">Mobile</dt>
                <dd className="text-ink">{order.contactMobile}</dd>
              </div>
              <div>
                <dt className="text-ink/50">WhatsApp</dt>
                <dd className="text-ink">{order.contactWhatsapp ?? '—'}</dd>
              </div>
              {order.requirementSummary && (
                <div className="sm:col-span-2">
                  <dt className="text-ink/50">Requirement</dt>
                  <dd className="text-ink">{order.requirementSummary}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Internal Notes</h2>
            <form onSubmit={handleAddNote} className="mt-3 flex gap-2">
              <Textarea rows={1} placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
              <Button type="submit" size="sm">
                Add
              </Button>
            </form>
            <div className="mt-4 space-y-2">
              {notes.length === 0 ? (
                <p className="text-sm text-ink/50">No notes yet.</p>
              ) : (
                notes.map((note) => (
                  <div key={note.id} className="rounded bg-paper p-3 text-sm">
                    <p className="text-ink">{note.note}</p>
                    <p className="mt-1 text-xs text-ink/40">{new Date(note.created_at).toLocaleString('en-IN')}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Update Status</h2>
            <Select
              className="mt-3"
              disabled={statusUpdating || allowedTransitions.length === 0}
              value=""
              onChange={(e) => e.target.value && handleStatusChange(e.target.value as OrderStatus)}
            >
              <option value="">{allowedTransitions.length === 0 ? 'No further transitions' : 'Select new status...'}</option>
              {allowedTransitions.map((status) => (
                <option key={status} value={status}>
                  {ORDER_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          </div>

          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Payment</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink/50">Subtotal</dt>
                <dd className="font-mono text-ink">₹{order.baseAmount.toLocaleString('en-IN')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink/50">Tax</dt>
                <dd className="font-mono text-ink">₹{order.taxAmount.toLocaleString('en-IN')}</dd>
              </div>
              <div className="stub-divider flex justify-between pt-2 font-semibold">
                <dt className="text-ink">Total</dt>
                <dd className="font-mono text-seal-blue">₹{order.totalAmount.toLocaleString('en-IN')}</dd>
              </div>
            </dl>
            <Button variant="outline" size="sm" className="mt-4 w-full" onClick={handleResendInvoice}>
              Resend Invoice
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
